import { imageCacheService } from '@services/ImageCacheService';
import { useEffect, useRef, useState } from 'react';

interface AuthenticatedImageProps {
  src: string;
  alt?: string;
  className?: string;
  width?: string | number;
  height?: string | number;
  token?: string | null;
  onError?: () => void;
  onLoad?: () => void;
  style?: React.CSSProperties;
}

export const AuthenticatedImage = ({
  src,
  alt,
  className,
  width,
  height,
  token,
  onError,
  onLoad,
  style,
}: AuthenticatedImageProps) => {
  // Synchronously initialise from cache to avoid a null→blob flash on remount.
  // Uses peekCachedImage (no refCount increment) — the useEffect handles acquire/release.
  const [imageData, setImageData] = useState<string | null>(() =>
    src && token ? imageCacheService.peekCachedImage(src, token) : null,
  );
  const [hasError, setHasError] = useState(false);
  const isMountedRef = useRef(true);
  // Ref so callers can pass an inline onLoad without it re-triggering the fetch effect.
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;

  useEffect(() => {
    isMountedRef.current = true;

    if (!src || !token) {
      setImageData(null);
      setHasError(false);
      return;
    }

    // If we already have cached data (from init or a previous render), skip the fetch
    const cached = imageCacheService.getCachedImage(src, token);
    if (cached) {
      setImageData(cached);
      setHasError(false);
      onLoadRef.current?.();
      return () => {
        isMountedRef.current = false;
        imageCacheService.releaseImage(src, token);
      };
    }

    const abortController = new AbortController();

    const loadImage = async () => {
      try {
        // Use the cache service to get the image
        // This will return a cached blob URL if available, or fetch and cache if not
        const blobUrl = await imageCacheService.getImage(src, token, abortController.signal);

        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setImageData(blobUrl);
          setHasError(false);
          onLoadRef.current?.();
        }
      } catch (error) {
        if ((error as Error).name === 'AbortError') return; // Expected when component unmounts or props change

        // For fetch failures (network errors, CORS issues, etc.), treat as if there's no thumbnail
        // This is graceful degradation - we just don't show an image rather than treating it as an error
        const errorMessage = (error as Error).message;
        if (errorMessage?.includes('Failed to fetch')) {
          if (isMountedRef.current) {
            console.debug('No thumbnail available (fetch failed):', errorMessage);
            setImageData(null);
            setHasError(false);
            onError?.();
          }
          return;
        }

        // For other errors, trigger the error callback
        if (isMountedRef.current) {
          console.error('Error loading authenticated image:', error);
          setHasError(true);
          onError?.();
        }
      }
    };

    loadImage();

    // Cleanup function to cancel fetch and release cache reference
    return () => {
      isMountedRef.current = false;
      abortController.abort();

      // Release the reference in the cache
      // The cache service will manage blob URL lifecycle
      if (token) {
        imageCacheService.releaseImage(src, token);
      }
    };
  }, [src, token, onError]);

  if (hasError || !imageData) {
    return null;
  }

  return (
    <img
      src={imageData}
      alt={alt}
      className={className}
      width={width}
      height={height}
      style={style}
    />
  );
};
